import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import { PageSizeService } from "@shared/services/page-size.service";
import { AgGridAngular } from "ag-grid-angular";
import {
  ColumnState,
  FilterChangedEvent,
  GridReadyEvent,
  PaginationChangedEvent,
  RowModelType,
  SelectionChangedEvent,
  StateUpdatedEvent
} from "ag-grid-community";
import { ColDef, GridApi } from "ag-grid-enterprise";
import { BulkChangeData } from "@models/ui-models/bulk-change-data";
import {
  AgCustomPaginationComponent
} from "@shared/components/grid/components/ag-custom-pagination/ag-custom-pagination.component";

@Component({
  selector: 'app-list-grid-client-side',
  standalone: true,
  imports: [
    AgCustomPaginationComponent,
    AgGridAngular
  ],
  providers: [{ provide: PageSizeService, useClass: PageSizeService }],
  templateUrl: 'list-grid-client-side.component.html',
})
export class ListGridClientSideComponent extends BaseListGridComponent implements OnInit, OnChanges {
  @Input() rowData: unknown[] = [];

  readonly rowModelType: RowModelType = 'clientSide';

  private gridApi!: GridApi;
  columnDefs: ColDef[] = [];
  currentPage = 0;
  totalPages = 0;
  pageNumbers: (number | string)[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['colDefs'] !== undefined && this.columnDefs.length && this.columnDefs !== changes['colDefs'].currentValue) {
      this.columnDefs = changes['colDefs'].currentValue;
    }
  }

  ngOnInit(): void {
    this.restoreGridColumnDefs();
  }

  restoreGridColumnDefs(): void {
    this.columnDefs = this.colDefs;
    const savedState = this.getSavedColumnState();

    if (savedState) {
      this.columnDefs.forEach((c: ColDef) => {
        const field = c.field ?? '';

        if (savedState) {
          const columnState = savedState.find((state: { colId: string }) => state.colId === field);
          if (columnState) {
            Object.assign(c, columnState);
          }
        }
      });

      this.columnDefs.sort((a, b) => {
        const indexA = savedState.findIndex(({ colId }) => colId === a.field) ?? Number.MAX_VALUE;
        const indexB = savedState.findIndex(({ colId }) => colId === b.field) ?? Number.MAX_VALUE;
        if (!a.field) return 1;
        return indexA - indexB;
      });
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api as unknown as GridApi;

    const savedState = this.getSavedColumnState();
    if (savedState && savedState.length === this.columnDefs.length) {
      params.api.applyColumnState({
        state: savedState,
        applyOrder: true,
      });
    }

    this.totalPages = this.gridApi.paginationGetTotalPages();
    this.pageNumbers = this.getPageNumbers();
    this.gridReady.emit(params.api);
  }

  restoreColumnState(): void {
    this.totalPages = this.gridApi.paginationGetTotalPages();
    this.pageNumbers = this.getPageNumbers();
  }

  //TODO refactor this
  onSelectionChanged(event: SelectionChangedEvent): void {
    const selectedRows = event.api.getSelectedRows();
    this.select.emit(selectedRows);

    //TODO refactor, should be check in upper components

    const isHeaderSource = event.source === 'uiSelectAll';

    if (isHeaderSource) {
      const selectedRows = event.api.getSelectedRows();
      const selectAllRows = (selectedRows as BulkChangeData[])?.length > 0;
      this.selectAll.emit(selectAllRows);
    }
  }

  onPaginationChanged(params: PaginationChangedEvent): void {
    if (this.paginationPageSize) {
      this.totalPages = params.api.paginationGetTotalPages();
      this.pageNumbers = this.getPageNumbers();
    }
  }

  onFilterChanged(event: FilterChangedEvent): void {
    this.totalPages = event.api.paginationGetTotalPages();
    this.currentPage = event.api.paginationGetCurrentPage();
    this.pageNumbers = this.getPageNumbers();
  }

  goToPreviousPage(): void {
    this.gridApi.paginationGoToPreviousPage();
    this.currentPage = this.gridApi.paginationGetCurrentPage();
    this.updatePageNumbers(this.currentPage + 1);
  }

  goToNextPage(): void {
    this.gridApi.paginationGoToNextPage();
    this.currentPage = this.gridApi.paginationGetCurrentPage();
    this.updatePageNumbers(this.currentPage + 1);
  }

  goToPage(page: number): void {
    this.gridApi.paginationGoToPage(page);
    this.currentPage = this.gridApi.paginationGetCurrentPage();
    this.updatePageNumbers(page + 1);
  }

  getPageNumbers(): (number | string)[] {
    const pageNumbers: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const halfMaxPagesToShow = Math.floor(maxPagesToShow / 2);
      const startPage = Math.max(1, this.currentPage - halfMaxPagesToShow);
      const endPage = Math.min(this.totalPages, this.currentPage + halfMaxPagesToShow);

      if (startPage > 1) {
        pageNumbers.push(1, '...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < this.totalPages) {
        pageNumbers.push('...', this.totalPages);
      }
    }

    return pageNumbers;
  }

  updatePageNumbers(pageNumber: number): void {
    if (pageNumber >= 1 && pageNumber <= this.totalPages) {
      this.pageNumbers = this.getPageNumbers();
    }
  }

  saveColumnState(): void {
    const storageKey = this.storageKey;
    if (storageKey) {
      const columnState = this.gridApi.getColumnState();
      localStorage.setItem(storageKey, JSON.stringify(columnState));
    }
  }

  getSavedColumnState(): ColumnState[] | null {
    const storageKey = this.storageKey;
    const columnState = storageKey ? localStorage.getItem(storageKey) : null;
    return columnState ? JSON.parse(columnState) : null;
  }

  onStateUpdated(params: StateUpdatedEvent<any>): void {
    this.saveColumnState();
  }
}
